/**
 * Image Quality Assessment for Document Capture
 * Client-side quality checks before sending to backend
 */

export interface QualityMetrics {
  sharpness: number;
  contrast: number;
  brightness: number;
  isBlurry: boolean;
  hasGlare: boolean;
  overallScore: number;
}

export interface QualityFeedback {
  isValid: boolean;
  message: string;
  recommendations: string[];
  quality: QualityMetrics;
}

/**
 * Calculate image sharpness using Laplacian variance
 * Higher values = sharper image
 */
export function calculateSharpness(imageData: ImageData): number {
  const { width, height, data } = imageData;
  
  // Convert to grayscale
  const gray = new Uint8Array(width * height);
  for (let i = 0; i < width * height; i++) {
    const idx = i * 4;
    gray[i] = Math.round(0.299 * data[idx] + 0.587 * data[idx + 1] + 0.114 * data[idx + 2]);
  }
  
  // Apply Laplacian kernel
  const laplacian = new Float32Array(width * height);
  let sum = 0;
  let sumSq = 0;
  let count = 0;
  
  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      const idx = y * width + x;
      
      // Laplacian kernel
      const val = 
        -1 * gray[idx - width] +
        -1 * gray[idx - 1] +
        4 * gray[idx] +
        -1 * gray[idx + 1] +
        -1 * gray[idx + width];
      
      laplacian[idx] = val;
      sum += val;
      sumSq += val * val;
      count++;
    }
  }
  
  // Calculate variance
  const mean = sum / count;
  const variance = (sumSq / count) - (mean * mean);
  
  return Math.abs(variance);
}

/**
 * Detect glare (overexposed areas)
 */
export function detectGlare(imageData: ImageData, threshold: number = 250): number {
  const { data } = imageData;
  let overexposed = 0;
  
  for (let i = 0; i < data.length; i += 4) {
    const brightness = (data[i] + data[i + 1] + data[i + 2]) / 3;
    if (brightness > threshold) {
      overexposed++;
    }
  }
  
  return overexposed / (data.length / 4);
}

/**
 * Calculate contrast (standard deviation)
 */
export function calculateContrast(imageData: ImageData): number {
  const { data } = imageData;
  let sum = 0;
  let sumSq = 0;
  const pixelCount = data.length / 4;
  
  for (let i = 0; i < data.length; i += 4) {
    const gray = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    sum += gray;
    sumSq += gray * gray;
  }
  
  const mean = sum / pixelCount;
  const variance = (sumSq / pixelCount) - (mean * mean);
  
  return Math.sqrt(variance);
}

/**
 * Calculate brightness (mean pixel value)
 */
export function calculateBrightness(imageData: ImageData): number {
  const { data } = imageData;
  let sum = 0;
  
  for (let i = 0; i < data.length; i += 4) {
    sum += (data[i] + data[i + 1] + data[i + 2]) / 3;
  }
  
  return sum / (data.length / 4);
}

/**
 * Full quality assessment.
 *
 * Threshold note: these cutoffs (sharpness<80, glare>15%, brightness
 * outside 50-200, contrast<30) are not calibrated against a specific input
 * resolution — Laplacian-variance sharpness in particular scales with image
 * size, so scoring a full-resolution capture vs. a downscaled copy can
 * shift the result. Caller (DocumentCapture._checkPhotoQuality) downscales
 * to a fixed 640px before calling this, so keep that pairing consistent —
 * if the downscale size changes, these thresholds should be re-checked
 * against real captures at the new size. This function is used as an
 * advisory-only nudge (never blocks submission), so a threshold that's
 * somewhat off costs an occasional wrong nudge, not a broken flow.
 */
export function assessQuality(imageData: ImageData): QualityFeedback {
  const sharpness = calculateSharpness(imageData);
  const glare = detectGlare(imageData);
  const contrast = calculateContrast(imageData);
  const brightness = calculateBrightness(imageData);
  
  const isBlurry = sharpness < 80;
  const hasGlare = glare > 0.15;
  const isTooDark = brightness < 50;
  const isTooBright = brightness > 200;
  const lowContrast = contrast < 30;
  
  // Calculate overall score (0-100)
  let score = 100;
  if (isBlurry) score -= 40;
  if (hasGlare) score -= 25;
  if (isTooDark) score -= 20;
  if (isTooBright) score -= 20;
  if (lowContrast) score -= 15;
  
  const metrics: QualityMetrics = {
    sharpness: Math.round(sharpness * 10) / 10,
    contrast: Math.round(contrast * 10) / 10,
    brightness: Math.round(brightness * 10) / 10,
    isBlurry,
    hasGlare,
    overallScore: Math.max(0, score),
  };
  
  // Generate feedback
  const issues: string[] = [];
  const recommendations: string[] = [];
  
  if (isBlurry) {
    issues.push("Too blurry");
    recommendations.push("Hold camera steady");
    recommendations.push("Ensure good lighting");
  }
  
  if (hasGlare) {
    issues.push("Too much glare");
    recommendations.push("Avoid direct light on document");
    recommendations.push("Tilt document slightly");
  }
  
  if (isTooDark) {
    issues.push("Too dark");
    recommendations.push("Move to brighter area");
    recommendations.push("Turn on more lights");
  }
  
  if (isTooBright) {
    issues.push("Too bright");
    recommendations.push("Reduce lighting");
    recommendations.push("Move away from direct sun");
  }
  
  if (lowContrast) {
    issues.push("Low contrast");
    recommendations.push("Ensure even lighting");
  }
  
  const isValid = score >= 60;
  const message = issues.length > 0 
    ? issues.join(", ")
    : "Good quality";
  
  return {
    isValid,
    message,
    recommendations: recommendations.slice(0, 3), // Max 3 recommendations
    quality: metrics,
  };
}

export interface FramingResult {
  /** True if a document-shaped region appears to fill the guide box. */
  isFramed: boolean;
  /** 0-1 — fraction of the guide box's border ring with a strong edge nearby. */
  borderEdgeCoverage: number;
  /** 0-1 — content variance inside the box relative to a blank/background frame. */
  interiorVariance: number;
}

/**
 * Lightweight, dependency-free framing heuristic — NOT real document/edge
 * detection (that needs actual computer vision, e.g. OpenCV.js). This is the
 * same class of check as assessQuality above: cheap enough to run every
 * ~300ms on a mid-range phone, good enough to catch the obvious failure
 * (no document in frame, badly cropped, document far outside the guide box),
 * not intended to catch subtle/partial cropping — that's still the backend
 * OCR + registry check's job.
 *
 * Approach: a real physical document produces a sharp intensity edge at its
 * own boundary. If the guide box is roughly aligned with a real document,
 * that edge should sit close to the guide box's own border. Sample a thin
 * ring around the guide box border and check edge strength there (Sobel-style
 * gradient, cheaper than the full Laplacian pass in calculateSharpness), plus
 * confirm the box interior isn't just blank/uniform background (a document
 * held further back than the guide, or no document at all, leaves the
 * interior looking like flat background/desk).
 *
 * guideBoxPct: the guide box's position as a fraction of imageData's full
 * width/height (0-1) — callers pass the SAME percentages used to size the
 * on-screen CSS guide box (see DocumentCapture's guideWrap), computed against
 * the raw video frame's own pixel dimensions so no CSS/object-fit mapping is
 * needed.
 */
export function assessFraming(
  imageData: ImageData,
  guideBoxPct: { x: number; y: number; width: number; height: number }
): FramingResult {
  const { width: W, height: H, data } = imageData;
  const gray = new Uint8Array(W * H);
  for (let i = 0; i < W * H; i++) {
    const idx = i * 4;
    gray[i] = Math.round(0.299 * data[idx] + 0.587 * data[idx + 1] + 0.114 * data[idx + 2]);
  }

  // edgeAt returns 0 for any pixel on the image's own outer boundary (no
  // neighbor on one side to diff against) — sampling the border ring
  // exactly AT that boundary (guideBoxPct spanning the full frame, e.g.
  // {0,0,1,1} for a manually uploaded photo with no guide overlay) means
  // every sample is structurally unmeasurable. Inset the sampled ring a
  // couple pixels from the box edges whenever the box reaches the image
  // boundary, so edgeAt always has real neighbor pixels to diff against —
  // this rescues the case where a document has its own printed border
  // sitting right at the frame edge.
  const EDGE_INSET = 2;
  const gx0 = Math.max(EDGE_INSET, Math.round(guideBoxPct.x * W));
  const gy0 = Math.max(EDGE_INSET, Math.round(guideBoxPct.y * H));
  const gx1 = Math.min(W - EDGE_INSET, Math.round((guideBoxPct.x + guideBoxPct.width) * W));
  const gy1 = Math.min(H - EDGE_INSET, Math.round((guideBoxPct.y + guideBoxPct.height) * H));

  // A genuinely full-bleed document — its own physical edge coincides
  // exactly with the photo's edge, no separate printed border graphic —
  // has NO detectable edge anywhere near the boundary: there's nothing to
  // contrast against by definition (no background, no frame line). The
  // inset above can't rescue this case; border-edge detection simply isn't
  // a meaningful signal here. Detected by: the guide box spans the whole
  // frame (nothing outside it to be "cropped from") AND content runs real,
  // varied pixels right up to all four true edges (not blank/background
  // fading out before the edge). When both hold, that's the best possible
  // framing outcome, not a failure — skip edge-ring scoring entirely for it.
  const isWholeFrameBox =
    guideBoxPct.x <= 0 && guideBoxPct.y <= 0 &&
    guideBoxPct.x + guideBoxPct.width >= 1 && guideBoxPct.y + guideBoxPct.height >= 1;

  const edgeAt = (x: number, y: number): number => {
    if (x < 1 || y < 1 || x >= W - 1 || y >= H - 1) return 0;
    const gxVal = gray[y * W + x + 1] - gray[y * W + x - 1];
    const gyVal = gray[(y + 1) * W + x] - gray[(y - 1) * W + x];
    return Math.sqrt(gxVal * gxVal + gyVal * gyVal);
  };

  const EDGE_THRESHOLD = 40;
  const BORDER_BAND = Math.max(4, Math.round(Math.min(W, H) * 0.015));

  // Sample points along the guide box border ring (top/bottom edges, left/right edges).
  let borderPoints = 0;
  let borderHits = 0;
  const SAMPLES_PER_SIDE = 24;

  const sampleBand = (fixed: number, isX: boolean, start: number, end: number) => {
    for (let s = 0; s < SAMPLES_PER_SIDE; s++) {
      const t = start + ((end - start) * s) / (SAMPLES_PER_SIDE - 1);
      let bestEdge = 0;
      for (let b = -BORDER_BAND; b <= BORDER_BAND; b++) {
        const x = isX ? fixed + b : Math.round(t);
        const y = isX ? Math.round(t) : fixed + b;
        bestEdge = Math.max(bestEdge, edgeAt(x, y));
      }
      borderPoints++;
      if (bestEdge >= EDGE_THRESHOLD) borderHits++;
    }
  };

  sampleBand(gy0, false, gx0, gx1); // top
  sampleBand(gy1, false, gx0, gx1); // bottom
  sampleBand(gx0, true, gy0, gy1);  // left
  sampleBand(gx1, true, gy0, gy1);  // right

  const borderEdgeCoverage = borderPoints > 0 ? borderHits / borderPoints : 0;

  // Shared: mean/variance of gray values over an arbitrary set of sampled
  // points, normalized against a "reasonable document photo" ballpark
  // std-dev (~35) so the 0-1 scale is comparable across lighting
  // conditions. Used both for the interior grid below and for each edge
  // strip's own content check.
  const sampledMeanAndVariance = (points: Array<[number, number]>): { mean: number; variance: number } => {
    let sum = 0, sumSq = 0, n = 0;
    for (const [x, y] of points) {
      if (x < 0 || y < 0 || x >= W || y >= H) continue;
      const v = gray[y * W + x];
      sum += v; sumSq += v * v; n++;
    }
    const mean = n > 0 ? sum / n : 0;
    const variance = n > 0 ? sumSq / n - mean * mean : 0;
    return { mean, variance: Math.min(1, Math.sqrt(Math.max(0, variance)) / 35) };
  };
  const sampledVariance = (points: Array<[number, number]>): number =>
    sampledMeanAndVariance(points).variance;

  // Interior variance — sample a coarse grid inside the box, well clear of
  // the border band, and check the spread of gray values isn't flat.
  const GRID = 12;
  const insetX = Math.round((gx1 - gx0) * 0.1);
  const insetY = Math.round((gy1 - gy0) * 0.1);
  const interiorPoints: Array<[number, number]> = [];
  for (let iy = 0; iy < GRID; iy++) {
    for (let ix = 0; ix < GRID; ix++) {
      interiorPoints.push([
        gx0 + insetX + Math.round(((gx1 - gx0 - 2 * insetX) * ix) / (GRID - 1)),
        gy0 + insetY + Math.round(((gy1 - gy0 - 2 * insetY) * iy) / (GRID - 1)),
      ]);
    }
  }
  const { mean: interiorMean, variance: interiorVariance } = sampledMeanAndVariance(interiorPoints);

  // For the whole-frame case: two real photos of genuinely well-framed,
  // uncropped documents were wrongly rejected by an earlier version of this
  // check that classified each edge strip against a FIXED brightness
  // threshold ("document margin" vs. "background") — real document margins
  // turned out to vary too much in absolute brightness (~206 to ~247 across
  // two ordinary phone photos of the same document type) for any single
  // global number to hold up; every fix attempt just relocated the false
  // positive to the next real photo tested.
  //
  // Replaced with a RELATIVE check instead of an absolute one: does each
  // edge strip's own tone sit reasonably close to the document's own
  // interior tone? A document's printed margin — however light, dark, or
  // off-white — is still part of the same physical object as its interior,
  // so the two should be in the same ballpark regardless of lighting. A
  // genuinely different scene bleeding into frame (desk, wall, skin,
  // fabric) is far more likely to diverge meaningfully from the document's
  // own interior tone than an off-white margin ever would. This self-
  // calibrates per photo instead of assuming one lighting condition.
  const EDGE_TONE_TOLERANCE = 60; // gray-level units either side of interiorMean
  let allEdgesMatchInteriorTone = isWholeFrameBox;
  if (isWholeFrameBox) {
    const EDGE_SAMPLE_DEPTH = Math.max(2, Math.round(Math.min(W, H) * 0.03));
    const edgeStripPoints = (fixed: number, isX: boolean, start: number, end: number): Array<[number, number]> => {
      const points: Array<[number, number]> = [];
      for (let s = 0; s < SAMPLES_PER_SIDE; s++) {
        const t = start + ((end - start) * s) / (SAMPLES_PER_SIDE - 1);
        for (let d = 0; d < EDGE_SAMPLE_DEPTH; d++) {
          const x = isX ? fixed + (fixed === 0 ? d : -d) : Math.round(t);
          const y = isX ? Math.round(t) : fixed + (fixed === 0 ? d : -d);
          points.push([x, y]);
        }
      }
      return points;
    };
    const sideCloseToInteriorTone = (points: Array<[number, number]>): boolean => {
      const { mean } = sampledMeanAndVariance(points);
      return Math.abs(mean - interiorMean) <= EDGE_TONE_TOLERANCE;
    };
    allEdgesMatchInteriorTone = [
      sideCloseToInteriorTone(edgeStripPoints(0, false, 0, W - 1)),      // top
      sideCloseToInteriorTone(edgeStripPoints(H - 1, false, 0, W - 1)),  // bottom
      sideCloseToInteriorTone(edgeStripPoints(0, true, 0, H - 1)),       // left
      sideCloseToInteriorTone(edgeStripPoints(W - 1, true, 0, H - 1)),   // right
    ].every(Boolean);
  }

  const isFullBleed = allEdgesMatchInteriorTone && interiorVariance >= 0.25;

  // Otherwise: require a solid majority of the border ring to show an edge
  // AND some real content inside — either alone can false-positive (a
  // plain wall has no edges but so does a document photographed at very
  // low contrast; a cluttered background has edges everywhere but nothing
  // document-shaped where the guide box actually is).
  const isFramed = isFullBleed || (borderEdgeCoverage >= 0.55 && interiorVariance >= 0.25);

  return { isFramed, borderEdgeCoverage, interiorVariance };
}

/**
 * Get quality color for UI feedback
 */
export function getQualityColor(score: number): string {
  if (score >= 80) return "#22c55e"; // Green
  if (score >= 60) return "#eab308"; // Yellow
  return "#ef4444"; // Red
}

/**
 * Get quality message for display
 */
export function getQualityMessage(score: number): string {
  if (score >= 80) return "Excellent - Ready to capture";
  if (score >= 60) return "Good - Can capture";
  if (score >= 40) return "Fair - Improve if possible";
  return "Poor - Please adjust";
}
